"use client";

import { cookedToPlainText } from "@/lib/processCooked";
import type { Conversation, Message } from "@/lib/api/messages";
import { firstName, personName } from "./participants";
import { RequestActions } from "./RequestActions";
import { SharedGroupChips } from "./SharedGroupChip";

/**
 * The accept gate, shown in place of the messages and the composer on a
 * conversation from someone the viewer has not written to before.
 *
 * OSO-351: the server already sends the message text for a pending request —
 * "withholding them is a presentation rule in the thread view, not an access
 * rule", which v1 states outright — the gate just used to never show it. It
 * now previews the opening message, so the viewer can judge the request before
 * accepting, without the sender being told either way.
 */
export function RequestBanner({
  conversation,
  messages,
}: {
  conversation: Conversation;
  messages: Message[];
}) {
  const senderName = personName(conversation.createdBy);

  // The system lines ("… blev tilføjet") are never what prompted the
  // request; the first REGULAR post is. There may be more than one message
  // waiting (the sender can write again before you decide) — the rest are
  // still hidden, previewing all of them would defeat the point of a gate
  // that is supposed to stay lightweight.
  const opening = messages.find((m) => m.kind === "regular");
  const preview = opening ? cookedToPlainText(opening.bodyHtml ?? "") : "";
  const extraCount = opening
    ? messages.filter((m) => m.kind === "regular").length - 1
    : 0;

  return (
    /**
     * min-w-0: THIS BANNER IS A FLEX ITEM ON THE HORIZONTAL AXIS. Its wrapper
     * in `ConversationThread` is `flex items-center` — a flex ROW — so this
     * div's `min-width` defaults to `auto`, which resolves to its MIN-CONTENT
     * width and floors how far it can shrink.
     *
     * The trap is that `break-words` on the preview below does NOT lower that
     * floor. `overflow-wrap: break-word` breaks a long chunk to avoid
     * overflowing a box it has already been given, but it leaves the box's
     * INTRINSIC min-content size at the full width of the unbroken chunk.
     * So the classes on the preview looked like they covered this and could
     * not: they only get to act once something else has decided the width.
     *
     * Measured on stage against the real class stack, one 300-character word
     * with no break opportunity, in a 544px column: the banner laid out at
     * 2188px — 1644px of it outside the column, taking the explanatory text
     * and the buttons with it. With `min-w-0` it lays out at exactly 544px and
     * the preview wraps inside it.
     *
     * NOT `w-full`: measured too, and it changes nothing. The explanatory
     * paragraph below is always long enough that the banner's max-content
     * exceeds this column, so it already fills the width on its own.
     *
     * SEE ALSO OSO-487, the same bug one surface over, in `MessageBubble`.
     * That one needed `max-w-full` rather than this, because the bubble is
     * shrink-to-fit and the fix had to cap a used width; here the item is
     * stretched by a min-width floor and the fix has to remove the floor.
     * Same symptom, opposite lever — which is why neither fix generalises to
     * the other and why they had to be found twice.
     */
    <div className="min-w-0 border-t border-border bg-indigo-50/50 dark:bg-indigo-950/50 p-4 space-y-3">
      <p className="text-sm text-foreground">
        <strong className="font-semibold">{senderName}</strong>{" "}
        {conversation.isGroup
          ? "vil starte en gruppesamtale med dig"
          : "har sendt dig en besked"}
      </p>

      {opening && (
        <div className="rounded-xl border border-border bg-surface px-3 py-2.5">
          <p className="text-sm text-foreground whitespace-pre-wrap break-words">
            {preview || "📷 Billede"}
          </p>
          {extraCount > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              +{extraCount} {extraCount === 1 ? "besked mere" : "beskeder mere"} venter
            </p>
          )}
        </div>
      )}

      <SharedGroupChips groups={conversation.sharedGroups} />

      <p className="text-xs text-muted-foreground">
        Hverken en accept eller et afvis giver {firstName(senderName)} besked
        om, hvad du valgte. Afviser du, forsvinder samtalen for altid.
      </p>

      <RequestActions conversation={conversation} />
    </div>
  );
}
